import Component from '@glimmer/component';
import { service } from '@ember/service';
import { hash } from '@ember/helper';
import { and, eq } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import { variation } from 'ember-launch-darkly';
import {
  HdsTextDisplay,
  HdsBadge,
  HdsTextBody,
  HdsButtonSet,
} from '@hashicorp/design-system-components/components';
import { optionForAuth0Error } from 'core/helpers';
import { GEOS } from 'core/utils/constants';
import AuthButton from 'hcp/components/auth-button';

export const CONNECTION_TYPE_EMAIL = 'email';
export const CONNECTION_METHOD = 'Username-Password-Authentication';
const { EU, US } = GEOS;

/**
 *
 * `error` will be a sign in error.
 * @argument error;
 * @type {String}
 *
 */

/**
 *
 * `connection` is a string specifying the type of auth connection
 * @argument connection;
 * @type {String}
 *
 */

/**
 *
 * `isSignUpPage` is a boolean and determines the headline and order of the
 * buttons.
 *
 * true: The sign-up headline renders, the create account button renders
 * first, the sign-in button renders second.
 *
 * false: The sign-in headline renders, the sign in button is first and the
 * create account button is second.
 *
 * @argument isSignUpPage;
 * @type {Boolean}
 *
 */
/**
 *
 * `PageSignInMain` renders the main content panel for the sign-in page and
 * redirects users to the auth provider.
 *
 *
 * ```
 * <Page::SignInMain
 *  @connection={{@connection}}
 *  @error={{@error}}
 *  @isSignUpPage={{true}}
 * />
 * ```
 *
 * @class PageSignInMain
 *
 */
export default class PageSignInMain extends Component {
  @service config;

  EU = EU;

  CONNECTION_TYPE_EMAIL = CONNECTION_TYPE_EMAIL;

  CONNECTION_METHOD = CONNECTION_METHOD;

  get authenticatorType() {
    return this.config.authenticatorType;
  }

  get deployGeo() {
    return this.config.app?.deployGeo ?? US;
  }

  get isEuPrivateBetaBadgeEnabled() {
    return variation('hcp-ui-eu-private-beta-badge');
  }

  <template>
    <div data-test-page-sign-in-main>
      {{#let (eq this.deployGeo this.EU) as |isEU|}}
        <HdsTextDisplay
          @tag="h1"
          data-test-page-sign-in-main-title
          class="font-hashicorp-sans"
        >
          {{#if @isSignUpPage}}
            {{t
              (if
                isEU
                "components.page.sign-up.main.title-eu"
                "components.page.sign-up.main.title"
              )
            }}
          {{else}}
            {{t
              (if
                isEU
                "components.page.sign-in.main.title-eu"
                "components.page.sign-in.main.title"
              )
            }}
          {{/if}}
          {{#if (and isEU this.isEuPrivateBetaBadgeEnabled)}}
            <HdsBadge
              @text={{t "components.page.sign-in.main.private-beta-badge"}}
              @color="highlight"
              @size="large"
            />
          {{/if}}
        </HdsTextDisplay>
        {{#if @error}}
          {{#let (optionForAuth0Error @error) as |option|}}
            <HdsTextBody
              class="hds-foreground-critical"
              data-sign-in-error-text
            >
              {{t option.message}}
            </HdsTextBody>
          {{/let}}
        {{/if}}
        <HdsButtonSet>
          {{#if @isSignUpPage}}
            {{#if (eq @connection this.CONNECTION_TYPE_EMAIL)}}
              <AuthButton
                @variant="primary"
                @text={{t "components.page.sign-up.create-an-account"}}
                @extraAuthArgs={{hash
                  screen_hint="signup"
                  connection=this.CONNECTION_METHOD
                }}
                data-test-page-sign-up-email-connection-button
                data-test-page-sign-up-button
              />
              <AuthButton
                @variant="secondary"
                @extraAuthArgs={{hash
                  mode="login"
                  connection=this.CONNECTION_METHOD
                }}
                data-test-page-sign-in-email-connection-button
                data-test-page-sign-in-button
              />
            {{else}}
              <AuthButton
                @variant="primary"
                @text={{t "components.page.sign-up.create-an-account"}}
                @extraAuthArgs={{hash screen_hint="signup"}}
                data-test-page-sign-up-no-connection-button
                data-test-page-sign-up-button
              />
              <AuthButton
                @variant="secondary"
                @extraAuthArgs={{hash mode="login"}}
                data-test-page-sign-in-no-connection-button
                data-test-page-sign-in-button
              />
            {{/if}}
          {{else}}
            {{#if (eq @connection this.CONNECTION_TYPE_EMAIL)}}
              <AuthButton
                @variant="primary"
                @extraAuthArgs={{hash
                  mode="login"
                  connection=this.CONNECTION_METHOD
                }}
                data-test-page-sign-in-email-connection-button
                data-test-page-sign-in-button
              />
              {{#unless isEU}}
                <AuthButton
                  @variant="secondary"
                  @text={{t "components.page.sign-up.create-an-account"}}
                  @extraAuthArgs={{hash
                    screen_hint="signup"
                    connection=this.CONNECTION_METHOD
                  }}
                  data-test-page-sign-up-email-connection-button
                  data-test-page-sign-up-button
                />
              {{/unless}}
            {{else}}
              <AuthButton
                @variant="primary"
                @extraAuthArgs={{hash mode="login"}}
                data-test-page-sign-in-no-connection-button
                data-test-page-sign-in-button
              />
              {{#unless isEU}}
                <AuthButton
                  @variant="secondary"
                  @text={{t "components.page.sign-up.create-an-account"}}
                  @extraAuthArgs={{hash screen_hint="signup"}}
                  data-test-page-sign-up-no-connection-button
                  data-test-page-sign-up-button
                />
              {{/unless}}
            {{/if}}

          {{/if}}
        </HdsButtonSet>
      {{/let}}
    </div>
  </template>
}
